/**
 * Canonical challenge lifecycle phase — Sprint 6 WI-1.
 *
 * The `challenges` collection has 10 raw statuses (`ChallengeStatus` in
 * `src/api/challengeService.js`, the single source of truth). Consumers across
 * the app have historically re-derived "is this active / terminal / claimable?"
 * from an ad-hoc mix of `status` + `finalOutcome` + `settlement`, sometimes
 * keying off statuses that no writer can produce.
 *
 * This module collapses raw status into ONE small phase vocabulary and exposes a
 * single predicate that every consumer imports, so classification lives in one
 * place. It is pure, additive, and side-effect free — no Firestore, no UI.
 */
import { ChallengeStatus } from '../api/challengeService';

/** Coarse lifecycle phase every consumer can classify against. */
export const ChallengePhase = {
  PENDING: 'pending', // awaiting accept (standard) or agreement (custom)
  ACTIVE: 'active', // locked and in play, no winner yet
  AWAITING_WINNER: 'awaiting_winner', // result/voting/dispute in flight, or winner determined but unclaimed
  SETTLED: 'settled', // funds released — terminal
  DECLINED: 'declined', // declined/cancelled before lock — terminal
  EXPIRED: 'expired', // aged out before acceptance — terminal (status added in WI-3)
};

/**
 * Raw status → phase. `EXPIRED` is mapped now so consumers are ready before
 * WI-3 introduces the persisted status; `REFUNDED` is a terminal, non-counting
 * refund (treated as `declined` for classification — WI-4 may refine display).
 */
const STATUS_TO_PHASE = {
  [ChallengeStatus.PENDING_ACCEPTANCE]: ChallengePhase.PENDING,
  [ChallengeStatus.PENDING_AGREEMENT]: ChallengePhase.PENDING,
  [ChallengeStatus.ACTIVE]: ChallengePhase.ACTIVE,
  [ChallengeStatus.LOCKED_IN]: ChallengePhase.ACTIVE,
  [ChallengeStatus.PENDING_WINNER]: ChallengePhase.AWAITING_WINNER,
  [ChallengeStatus.SECOND_CHANCE]: ChallengePhase.AWAITING_WINNER,
  [ChallengeStatus.DISPUTED]: ChallengePhase.AWAITING_WINNER,
  [ChallengeStatus.SETTLED]: ChallengePhase.SETTLED,
  [ChallengeStatus.DECLINED]: ChallengePhase.DECLINED,
  [ChallengeStatus.REFUNDED]: ChallengePhase.DECLINED,
  EXPIRED: ChallengePhase.EXPIRED,
};

const TERMINAL_PHASES = new Set([
  ChallengePhase.SETTLED,
  ChallengePhase.DECLINED,
  ChallengePhase.EXPIRED,
]);

const ACTIVE_PHASES = new Set([
  ChallengePhase.ACTIVE,
  ChallengePhase.AWAITING_WINNER,
]);

/**
 * Resolve a challenge's canonical phase.
 *
 * Field overrides matter because terminal truth can land on a field before the
 * status string catches up:
 * - `settlement` present → funds released → `settled` (server terminal truth).
 * - `finalOutcome` present (winner determined, not yet settled) → `awaiting_winner`
 *   (e.g. live challenges keep `status: ACTIVE` while the winner can claim).
 * Declined/refunded/expired statuses are checked first since they never carry an
 * outcome.
 *
 * @param {object|null|undefined} bet — challenge record (expects `status`)
 * @returns {string|null} a ChallengePhase value, or null when unclassifiable
 */
export function challengePhase(bet) {
  if (!bet || typeof bet !== 'object') return null;
  const status = bet.status;

  if (status === ChallengeStatus.DECLINED || status === ChallengeStatus.REFUNDED) {
    return STATUS_TO_PHASE[status];
  }
  if (status === 'EXPIRED') return ChallengePhase.EXPIRED;

  if (bet.settlement || status === ChallengeStatus.SETTLED) return ChallengePhase.SETTLED;
  if (bet.finalOutcome) return ChallengePhase.AWAITING_WINNER;

  return STATUS_TO_PHASE[status] ?? null;
}

/** @param {string|null} phase @returns {boolean} terminal (settled/declined/expired) */
export function isTerminalPhase(phase) {
  return TERMINAL_PHASES.has(phase);
}

/** @param {string|null} phase @returns {boolean} in play (active/awaiting_winner) */
export function isActivePhase(phase) {
  return ACTIVE_PHASES.has(phase);
}

/** Convenience: classify a bet directly. */
export function isTerminalChallenge(bet) {
  return isTerminalPhase(challengePhase(bet));
}

/** Convenience: classify a bet directly. */
export function isActiveChallenge(bet) {
  return isActivePhase(challengePhase(bet));
}
